import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert, Tab, Tabs, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { BsArrowLeft, BsPencil, BsFileText, BsBell, BsPerson, BsClock, BsCheckCircle } from 'react-icons/bs';
import { toast } from 'react-toastify';
import Breadcrumbs from '../../components/layout/breadcrumbs';
import Page from '../../components/page';
import { api } from '../../providers/apiClient';
import { ContextUser } from '../../providers/ContextUser';
import { IncidentForm, IncidentTimeline, IncidentReport, SDTimelineItem, SDTimelineContent, SDTimelineTitle, SDTimelineDescription, SDTimelineTime, CustomBadgeStyles, DetailActionButton } from './styles';
import { buildOrgLevelBreadcrumbs } from '../../utils/breadcrumbUtils';
import { formatIncidentCode } from '../../utils/formatCodes';
import { useConfirmToast } from '../../hooks/useConfirmToast';
import { generateIncidentReport } from '../../utils/incidentReport';

const IncidentDetails = ({ isCollapsed }) => {
  const { id, orgId } = useParams();
  const navigate = useNavigate();
  const { user_id, currentOrganization } = useContext(ContextUser);
  const { confirm } = useConfirmToast();
  const [incident, setIncident] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [newStatus, setNewStatus] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isStatusNoteModalOpen, setIsStatusNoteModalOpen] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [history, setHistory] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  useEffect(() => {
    loadIncident();
  }, [id]);
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab]);

  const fetchHistory = async () => {
    try {
      setIsLoadingHistory(true);
      const response = await api.get(`/incidents/${id}/history`);
      setHistory(response.data);
    } catch (error) {
      console.error('Erro ao buscar histórico do incidente:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };


  // Configurar breadcrumbs quando o incidente estiver carregado
  useEffect(() => {
    if (incident && currentOrganization?.id) {
      const breadcrumbs = buildOrgLevelBreadcrumbs(
        currentOrganization,
        formatIncidentCode(incident.id),
        `/prefeitura/${orgId}/incidents/${id}`
      );
      setBreadcrumbs(breadcrumbs);
      setNewStatus(incident.status || '');
    }
  }, [incident, currentOrganization, orgId, id]);

  const loadIncident = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/incidents/${id}`);
      setIncident(response.data);
    } catch (error) {
      console.error('Erro ao carregar incidente:', error);
      setError('Erro ao carregar incidente');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/prefeitura/${orgId}/incidents/${id}/edit`);
  };

  // Removido: ação de exclusão de incidente

  const handleGenerateReport = async () => {
    try {
      setIsGeneratingReport(true);
      
      // Gerar relatório usando os dados já carregados do incidente
      await generateIncidentReport(incident, api);
      
      // Adicionar um pequeno delay para garantir que o spinner seja visível
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error(`Erro ao gerar relatório: ${error.message}`);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleNotifyANPD = () => {
    confirm(
      'Tem certeza que deseja notificar a ANPD sobre este incidente?',
      confirmNotifyANPD,
      null,
      {
        confirmText: "Sim, notificar!",
        cancelText: "Cancelar",
        variant: "warning"
      }
    );
  };

  const confirmNotifyANPD = async () => {
      try {
        await api.post(`/incidents/${id}/notify-anpd`, {
          notificationDetails: {
            notifiedBy: user_id,
            notificationDate: new Date().toISOString()
          }
        });
        loadIncident(); // Recarregar dados
      toast.success('ANPD notificada com sucesso!');
      } catch (error) {
        console.error('Erro ao notificar ANPD:', error);
      toast.error('Erro ao notificar ANPD. Tente novamente.');
    }
  };

  const handleNotifySubjects = () => {
    confirm(
      'Tem certeza que deseja notificar os titulares de dados sobre este incidente?',
      confirmNotifySubjects,
      null,
      {
        confirmText: "Sim, notificar!",
        cancelText: "Cancelar",
        variant: "warning"
      }
    );
  };

  const confirmNotifySubjects = async () => {
      try {
        await api.post(`/incidents/${id}/notify-subjects`, {
          notificationDetails: {
            notifiedBy: user_id,
            notificationDate: new Date().toISOString()
          }
        });
        loadIncident(); // Recarregar dados
      toast.success('Titulares notificados com sucesso!');
      } catch (error) {
        console.error('Erro ao notificar titulares:', error);
      toast.error('Erro ao notificar titulares. Tente novamente.');
    }
  };

  const handleUpdateStatus = () => {
    if (!newStatus || !incident) return;
    if (newStatus === incident.status) {
      toast.info('O status selecionado é igual ao atual.');
      return;
    }
    // Abrir modal para coletar observação obrigatória
    setIsStatusNoteModalOpen(true);
  };

  const confirmUpdateStatus = async () => {
    try {
      if (!statusNote || statusNote.trim().length < 3) {
        toast.warn('Informe uma observação (mínimo 3 caracteres).');
        return;
      }
      setIsUpdatingStatus(true);
      await api.patch(`/incidents/${id}/status`, { status: newStatus, note: statusNote.trim() });
      await loadIncident();
      if (activeTab === 'history') {
        await fetchHistory();
      }
      toast.success('Status atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar status do incidente:', error);
      toast.error('Erro ao atualizar status. Tente novamente.');
    } finally {
      setIsUpdatingStatus(false);
      setIsStatusNoteModalOpen(false);
      setStatusNote('');
    }
  };

  

  const getSeverityColor = (severity) => {
    const colors = {
      BAIXA: 'success',
      MEDIA: 'warning',
      ALTA: 'danger',
      CRITICA: 'dark'
    };
    return colors[severity] || 'secondary';
  };

  const getStatusColor = (status) => {
    const colors = {
      ABERTO: 'danger',
      EM_ANALISE: 'warning',
      CONCLUIDO: 'success',
      ARQUIVADO: 'secondary'
    };
    return colors[status] || 'secondary';
  };

  const getIncidentTypeText = (type) => {
    const types = {
      VAZAMENTO_DADOS: 'Vazamento de Dados',
      ACESSO_NAO_AUTORIZADO: 'Acesso Não Autorizado',
      PERDA_DADOS: 'Perda de Dados',
      ALTERACAO_NAO_AUTORIZADA: 'Alteração Não Autorizada',
      DESTRUICAO_DADOS: 'Destruição de Dados',
      OUTROS: 'Outros'
    };
    return types[type] || type;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <Page id='page-incident-details' title='Carregando...'>
        <div className='text-center py-5'>
          <div className='spinner-border' role='status'>
            <span className='visually-hidden'>Carregando...</span>
          </div>
        </div>
      </Page>
    );
  }

  if (error || !incident) {
    return (
      <Page id='page-incident-details' title='Erro'>
        <Alert variant='danger'>
          {error || 'Incidente não encontrado'}
        </Alert>
      </Page>
    );
  }

  return (
    <>
      <Breadcrumbs id='breadcrumbs-incident-details' path={breadcrumbs} />
      <CustomBadgeStyles>
        <Page id='page-incident-details' title={`Incidente ${formatIncidentCode(incident.id)}`}>
        <Container fluid>
          {/* Modal de observação para alteração de status */}
          <div className={`modal ${isStatusNoteModalOpen ? 'd-block' : ''}`} tabIndex='-1' role='dialog' style={{ backgroundColor: isStatusNoteModalOpen ? 'rgba(0,0,0,0.5)' : 'transparent' }}>
            <div className='modal-dialog' role='document'>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title'>Alterar status para {newStatus}</h5>
                  <button type='button' className='btn-close' aria-label='Close' onClick={() => { setIsStatusNoteModalOpen(false); setStatusNote(''); }} />
                </div>
                <div className='modal-body'>
                  <p>Informe uma observação para registrar na auditoria:</p>
                  <textarea
                    className='form-control'
                    rows='4'
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    placeholder='Descreva o motivo da alteração de status...'
                  />
                </div>
                <div className='modal-footer'>
                  <button type='button' className='btn btn-secondary' onClick={() => { setIsStatusNoteModalOpen(false); setStatusNote(''); }}>Cancelar</button>
                  <button type='button' className='btn btn-primary' onClick={confirmUpdateStatus} disabled={isUpdatingStatus}>
                    {isUpdatingStatus ? 'Salvando...' : 'Confirmar alteração'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <Row>
            <Col>
              <Card>
                <Card.Header className='d-flex justify-content-between align-items-center'>
                  <div>
                    <h5 className='mb-0'>{incident.title}</h5>
                    <small className='text-muted'>{formatIncidentCode(incident.id)}</small>
                  </div>
                  <div className='d-flex gap-2'>
                    <DetailActionButton
                      className='action-button info'
                      onClick={handleGenerateReport}
                      id='generate-report-btn'
                      disabled={isGeneratingReport}
                      title='Gerar relatório PDF do incidente'
                    >
                      {isGeneratingReport ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            style={{
                              width: '16px',
                              height: '16px',
                            }}
                          />
                          Gerando
                        </>
                      ) : (
                        <>
                          <BsFileText />
                          Relatório do Incidente
                        </>
                      )}
                    </DetailActionButton>
                    <DetailActionButton
                      className='action-button secondary'
                      onClick={() => navigate(`/prefeitura/${orgId}/incidents`)}
                      id='back-incidents-btn'
                      title='Voltar para lista de incidentes'
                    >
                      <BsArrowLeft />
                      Voltar
                    </DetailActionButton>
                    <DetailActionButton
                      className='action-button secondary'
                      onClick={handleEdit}
                      id='edit-incident-btn'
                      title='Editar incidente'
                    >
                      <BsPencil />
                      Editar
                    </DetailActionButton>
                    {/* Ação de exclusão removida */}
                  </div>
                </Card.Header>
                
                <Card.Body>
                  <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className='mb-3'
                    id='incident-tabs'
                  >
                    <Tab eventKey='details' title='Detalhes'>
                      <Row>
                        <Col md={8}>
                          {/* Informações Básicas */}
                          <Card className='mb-4'>
                            <Card.Header>
                              <h6 className='mb-0'>Informações Básicas</h6>
                            </Card.Header>
                            <Card.Body>
                              <Row>
                                <Col md={6}>
                                  <div className='mb-3'>
                                    <strong>Status:</strong>
                                    <Badge 
                                      bg={getStatusColor(incident.status)} 
                                      className={`ms-2 status-${incident.status}`}
                                    >
                                      {incident.status}
                                    </Badge>
                                  </div>
                                  <div className='mb-3'>
                                    <strong>Severidade:</strong>
                                    <Badge 
                                      bg={getSeverityColor(incident.severityLevel)} 
                                      className='ms-2'
                                    >
                                      {incident.severityLevel}
                                    </Badge>
                                  </div>
                                  <div className='mb-3'>
                                    <strong>Tipo:</strong> {getIncidentTypeText(incident.incidentType)}
                                  </div>
                                </Col>
                                <Col md={6}>
                                  <div className='mb-3'>
                                    <strong>Data do Incidente:</strong>
                                    <br />
                                    <BsClock className='me-1' />
                                    {formatDate(incident.incidentDate)}
                                  </div>
                                  <div className='mb-3'>
                                    <strong>Data de Descoberta:</strong>
                                    <br />
                                    <BsClock className='me-1' />
                                    {formatDate(incident.discoveryDate)}
                                  </div>
                                  <div className='mb-3'>
                                    <strong>Data do Relatório:</strong>
                                    <br />
                                    <BsClock className='me-1' />
                                    {formatDate(incident.reportDate)}
                                  </div>
                                </Col>
                              </Row>
                              
                              <div className='mb-3'>
                                <strong>Descrição:</strong>
                                <p className='mt-2'>{incident.description}</p>
                              </div>
                            </Card.Body>
                          </Card>

                          {/* Dados Afetados */}
                          <Card className='mb-4'>
                            <Card.Header>
                              <h6 className='mb-0'>Dados Afetados</h6>
                            </Card.Header>
                            <Card.Body>
                              <Row>
                                <Col md={6}>
                                  <div className='mb-3'>
                                    <strong>Categorias de Dados:</strong>
                                    <div className='mt-1 d-flex flex-wrap gap-1'>
                                      {incident.dataCategories && incident.dataCategories.length > 0 ? (
                                        incident.dataCategories.map((category, index) => (
                                          <Badge key={index} bg='info' className='mb-1'>
                                            {category}
                                          </Badge>
                                        ))
                                      ) : (
                                        <span className='text-muted'>N/A</span>
                                      )}
                                    </div>
                                  </div>
                                </Col>
                                <Col md={6}>
                                  <div className='mb-3'>
                                    <strong>Titulares Afetados:</strong>
                                    <br />
                                    {incident.dataSubjectsCount || 0} pessoas
                                  </div>
                                </Col>
                              </Row>
                              
                              {incident.affectedSystems && incident.affectedSystems.length > 0 && (
                                <div className='mb-3'>
                                  <strong>Sistemas Afetados:</strong>
                                  <div className='mt-1 d-flex flex-wrap gap-1'>
                                    {incident.affectedSystems.map((system, index) => (
                                      <Badge key={index} bg='secondary' className='mb-1 systems-affected'>
                                        {system}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </Card.Body>
                          </Card>

                          {/* Detalhes Técnicos */}
                          {(incident.rootCause || incident.technicalDetails) && (
                            <Card className='mb-4'>
                              <Card.Header>
                                <h6 className='mb-0'>Detalhes Técnicos</h6>
                              </Card.Header>
                              <Card.Body>
                                {incident.rootCause && (
                                  <div className='mb-3'>
                                    <strong>Causa Raiz:</strong>
                                    <p className='mt-2'>{incident.rootCause}</p>
                                  </div>
                                )}
                                
                                {incident.technicalDetails && (
                                  <div className='mb-3'>
                                    <strong>Detalhes Técnicos:</strong>
                                    <p className='mt-2'>{incident.technicalDetails}</p>
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          )}

                          {/* Medidas Tomadas */}
                          {(incident.immediateMeasures || incident.containmentMeasures || 
                            incident.recoveryMeasures || incident.preventiveMeasures) && (
                            <Card className='mb-4'>
                              <Card.Header>
                                <h6 className='mb-0'>Medidas Tomadas</h6>
                              </Card.Header>
                              <Card.Body>
                                {incident.immediateMeasures && (
                                  <div className='mb-3'>
                                    <strong>Medidas Imediatas:</strong>
                                    <p className='mt-2'>{incident.immediateMeasures}</p>
                                  </div>
                                )}
                                
                                {incident.containmentMeasures && (
                                  <div className='mb-3'>
                                    <strong>Medidas de Contenção:</strong>
                                    <p className='mt-2'>{incident.containmentMeasures}</p>
                                  </div>
                                )}
                                
                                {incident.recoveryMeasures && (
                                  <div className='mb-3'>
                                    <strong>Medidas de Recuperação:</strong>
                                    <p className='mt-2'>{incident.recoveryMeasures}</p>
                                  </div>
                                )}
                                
                                {incident.preventiveMeasures && (
                                  <div className='mb-3'>
                                    <strong>Medidas Preventivas:</strong>
                                    <p className='mt-2'>{incident.preventiveMeasures}</p>
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          )}
                        </Col>
                        
                        <Col md={4}>
                          {/* Novo: Controle de Status do Incidente */}
                          <Card className='mb-4'>
                            <Card.Header>
                              <h6 className='mb-0'>Status do Incidente</h6>
                            </Card.Header>
                            <Card.Body>
                              <div className='mb-3'>
                                <strong>Status Atual:</strong>
                                <Badge 
                                  bg={getStatusColor(incident.status)} 
                                  className={`ms-2 status-${incident.status}`}
                                  id='current-status-badge'
                                >
                                  {incident.status}
                                </Badge>
                              </div>

                              <div className='mb-3'>
                                <label htmlFor='status-select' className='form-label'><strong>Novo Status</strong></label>
                                <select
                                  id='status-select'
                                  className='form-select'
                                  value={newStatus}
                                  onChange={(e) => setNewStatus(e.target.value)}
                                >
                                  <option value=''>Selecione</option>
                                  <option value='ABERTO'>Aberto</option>
                                  <option value='EM_ANALISE'>Em Análise</option>
                                  <option value='CONCLUIDO'>Concluído</option>
                                  <option value='ARQUIVADO'>Arquivado</option>
                                </select>
                              </div>

                              <div className='d-grid'>
                                <Button
                                  variant='outline-primary'
                                  onClick={handleUpdateStatus}
                                  disabled={isUpdatingStatus || !newStatus}
                                  id='update-status-btn'
                                >
                                  {isUpdatingStatus ? 'Atualizando...' : 'Atualizar Status'}
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>

                          {/* Status de Notificações */}
                          <Card className='mb-4'>
                            <Card.Header>
                              <h6 className='mb-0'>Status de Notificações</h6>
                            </Card.Header>
                            <Card.Body>
                              <div className='mb-3'>
                                <div className='d-flex align-items-center justify-content-between'>
                                  <span>
                                    <BsBell className='me-2' />
                                    ANPD Notificada
                                  </span>
                                  {incident.anpdNotified ? (
                                    <Badge bg='success'>
                                      <BsCheckCircle className='me-1' />
                                      Sim
                                    </Badge>
                                  ) : (
                                    <Badge bg='warning'>Não</Badge>
                                  )}
                                </div>
                                {incident.anpdNotificationDate && (
                                  <small className='text-muted'>
                                    {formatDate(incident.anpdNotificationDate)}
                                  </small>
                                )}
                              </div>
                              
                              <div className='mb-3'>
                                <div className='d-flex align-items-center justify-content-between'>
                                  <span>
                                    <BsPerson className='me-2' />
                                    Titulares Notificados
                                  </span>
                                  {incident.dataSubjectsNotified ? (
                                    <Badge bg='success'>
                                      <BsCheckCircle className='me-1' />
                                      Sim
                                    </Badge>
                                  ) : (
                                    <Badge bg='warning'>Não</Badge>
                                  )}
                                </div>
                                {incident.dataSubjectsNotificationDate && (
                                  <small className='text-muted'>
                                    {formatDate(incident.dataSubjectsNotificationDate)}
                                  </small>
                                )}
                              </div>
                              
                              <div className='d-grid gap-2'>
                                {!incident.anpdNotified && (
                                  <Button
                                    variant='outline-primary'
                                    size='sm'
                                    onClick={handleNotifyANPD}
                                    id='notify-anpd-btn'
                                    disabled
                                  >
                                    <BsBell className='me-2' />
                                    Notificar ANPD
                                  </Button>
                                )}
                                
                                {!incident.dataSubjectsNotified && (
                                  <Button
                                    variant='outline-info'
                                    size='sm'
                                    onClick={handleNotifySubjects}
                                    id='notify-subjects-btn'
                                    disabled
                                  >
                                    <BsPerson className='me-2' />
                                    Notificar Titulares
                                  </Button>
                                )}
                              </div>
                            </Card.Body>
                          </Card>

                          

                          {/* Informações da Organização */}
                          <Card className='mb-4'>
                            <Card.Header>
                              <h6 className='mb-0'>Informações da Organização</h6>
                            </Card.Header>
                            <Card.Body>
                              <div className='mb-3'>
                                <strong>Organização:</strong>
                                <br />
                                {incident.org?.name || 'N/A'}
                              </div>
                              
                              {incident.branch && (
                                <div className='mb-3'>
                                  <strong>Filia/Secretaria:</strong>
                                  <br />
                                  {incident.branch.name}
                                </div>
                              )}
                              
                              {incident.unit && (
                                <div className='mb-3'>
                                  <strong>Unidade:</strong>
                                  <br />
                                  {incident.unit.name}
                                </div>
                              )}
                              
                              {incident.department && (
                                <div className='mb-3'>
                                  <strong>Departamento:</strong>
                                  <br />
                                  {incident.department.name}
                                </div>
                              )}
                              
                              <div className='mb-3'>
                                <strong>DPO Responsável:</strong>
                                <br />
                                <BsPerson className='me-1' />
                                {incident.dpo?.name || 'N/A'}
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Tab>
                    <Tab eventKey='history' title={<span>Histórico</span>}>
                      <Row>
                        <Col md={12}>
                          <Card className='mb-4'>
                            <Card.Body>
                              {isLoadingHistory ? (
                                <div className='text-center py-4'>
                                  <Spinner animation='border' size='sm' /> Carregando histórico...
                                </div>
                              ) : history ? (
                                <div id='incident-history-timeline'>
                                  {history.timeline && history.timeline.length > 0 ? (
                                    history.timeline.map((item, index) => {
                                      const isStatusChange = item.type === 'status_changed' || !!(item.fromStatus || item.from || item.toStatus || item.to);
                                      const isComment = item.type === 'comment_added' || item.type === 'comment_edited' || item.type === 'comment_deleted';
                                      const title = 
                                        item.type === 'created' ? 'Chamado criado' :
                                        item.type === 'comment_added' ? 'Comentário adicionado' :
                                        item.type === 'comment_edited' ? 'Comentário editado' :
                                        item.type === 'comment_deleted' ? 'Comentário deletado' :
                                        item.type === 'status_changed' ? 'Status alterado' :
                                        item.title || item.action || 'Evento';
                                      const description = item.description || item.note || '';
                                      const observation = (
                                        item.observation ||
                                        item.observacao ||
                                        item.statusNote ||
                                        item.note ||
                                        (item.details && item.details.note) ||
                                        (item.metadata && item.metadata.note) ||
                                        item.reason ||
                                        item.message ||
                                        ''
                                      );
                                      const author = item.user?.name || item.author?.name || item.author || '';
                                      const oldValue = item.oldValue || item.fromStatus || item.from || null;
                                      const newValue = item.newValue || item.toStatus || item.to || null;
                                      const timestamp = item.timestamp || item.date || item.createdAt;
                                      return (
                                        <SDTimelineItem
                                          key={`history-item-${index}`}
                                          id={`history-item-${index}`}
                                          className={`${isComment ? 'comment-item' : ''} ${isStatusChange ? 'status-change' : ''}`}
                                        >
                                          <SDTimelineContent>
                                            <SDTimelineTitle>{title}</SDTimelineTitle>
                                            {description && (
                                              <SDTimelineDescription>{description}</SDTimelineDescription>
                                            )}
                                            {oldValue && newValue && (
                                              <div className='mt-2 p-2 bg-light rounded'>
                                                <div className='text-muted small'>De:</div>
                                                <div className='text-decoration-line-through text-muted'>{oldValue}</div>
                                                <div className='text-muted small mt-1'>Para:</div>
                                                <div className='text-success'>{newValue}</div>
                                              </div>
                                            )}
                                            {newValue && !oldValue && (
                                              <div className='mt-2 p-2 bg-light rounded' style={{ whiteSpace: 'pre-wrap' }}>
                                                {newValue}
                                              </div>
                                            )}
                                            {observation && (
                                              <div className='mt-2 p-2 bg-light rounded' id={`history-item-${index}-observation`} style={{ whiteSpace: 'pre-wrap' }}>
                                                <div className='text-muted small'>Observação:</div>
                                                {observation}
                                              </div>
                                            )}
                                            <SDTimelineTime id={`history-item-${index}-meta`}>
                                              {author ? `Por ${author} em ` : ''}{new Date(timestamp).toLocaleString('pt-BR')}
                                            </SDTimelineTime>
                                          </SDTimelineContent>
                                        </SDTimelineItem>
                                      );
                                    })
                                  ) : (
                                    <div className='text-muted' id='history-empty'>Nenhum evento no histórico.</div>
                                  )}
                                </div>
                              ) : (
                                <div className='text-muted'>Nenhum histórico disponível.</div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Tab>
                  </Tabs>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
        </Page>
      </CustomBadgeStyles>
    </>
  );
};

export default IncidentDetails;
